import "keen-slider/keen-slider.min.css";
import React from "react";
import { IoIosCheckmarkCircle } from "react-icons/io";
import { CasestudyCard, CasestudyCardProps } from ".";
import NewLayout from "../../components/new-ui/Layout";

import {
    CaseStudyHerosection,
    CaseStudyInformation,
    CaseStudySubInformation,
    ClientFeedback,
    FeatureList,
    Navbar,
    navLinks,
    OrganizationProfile,
} from "./alrannan-administrative-services";
import StatsInfo from "@components/new-ui/IndustriesSection/StatsInfo";

const casestudyCardsDetails: CasestudyCardProps[] = [
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/alrannan-admin-service.png",
        title: "Success Story : Improved Transparency and Increased Conversions",
        sector: "Education",
        externalLink: "/case-study/alrannan-administrative-services",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/goireland.png",
        title: "Success Story : Improved Transparency and Increased Conversions",
        sector: "Education",
        externalLink: "/case-study/goireland",
    },
    {
        imageUrl: "https://assets.gallabox.com/gb-home/case-study/dubai-housekeeping.png",
        title: "Gallabox transforms Dubai Housekeeping's journey to service excellence on WhatsApp",
        sector: "Service",
        externalLink: "/case-study/dubai-housekeeping",
    },
];

const qatarInsurance: React.FC = () => {
    return (
        <NewLayout
            title="Qatar Insurance Company | WhatsApp automation customer story"
            description="From doubling leads to improving CSAT – find out how Qatar Insurance Company transformed their WhatsApp Business automation game with Gallabox."
            canonical="https://gallabox.com/case-study/qatar-insurance-company"
            ogAttributes={{ image: "https://assets.gallabox.com/gb-home/case-study/qatar-insurance-company-og.png" }}
        >
            <CaseStudyHerosection
                title="How Gallabox Helped Qatar Insurance Company Generate 2X More Leads on WhatsApp"
                description="From doubling leads to improving CSAT – find out how Qatar Insurance Company transformed their WhatsApp Business automation game with Gallabox. ."
                imageUrl={"https://assets.gallabox.com/gb-home/case-study/qatar-insurance-company.png"}
                redirectUrl={"/case-study"}
            />

            <StatsInfo
                statsInfo={[
                    { percentage: "31%", label: "Increase in team productivity" },
                    { percentage: "102%", label: "Growth in lead generation" },
                    { percentage: "2x", label: "Improvement in CSAT ratings" },
                ]}
            />
            <section className="max-w-6xl mx-auto  md:grid md:grid-cols-6 mt-24 mb-24">
                <div className="bg-white col-span-1">
                    <div style={{ position: "sticky", top: "100px" }}>
                        <OrganizationProfile
                            companyProfileData={[
                                { label: "Company", value: ["Qatar Insurance Company"] },
                                { label: "Industry", value: ["Finance and Banking"] },
                                { label: "Employees", value: ["1,001-5,000"] },
                                { label: "Team", value: ["Sales", "Support", "Marketing"] },
                            ]}
                            product={{
                                title: "Features used",
                                productLink: [
                                    { link: "https://gallabox.com/whatsapp-integrations", label: "Integrations" },
                                    { link: "https://gallabox.com/whatsapp-chatbot", label: "WhatsApp Chatbot" },
                                    { link: "https://gallabox.com/whatsapp-flows", label: "WhatsApp Flows" },
                                    { link: "https://gallabox.com/whatsapp-broadcast", label: "WhatsApp Broadcast" },
                                    { link: "https://gallabox.com/click-to-whatsapp-ads", label: "Click-to-WhatsApp Ads" },
                                ],
                            }}
                        />
                    </div>
                </div>
                <div className=" col-span-4 md:pl-8 px-4">
                    <div className="flex flex-col gap-y-12">
                        <section id="About">
                            <CaseStudyInformation mainTitle={"A pioneer of domestic insurance companies in Qatar"} className="mt-0" />
                            <p className="mt-2">
                                Qatar Insurance Company has been a driving force in the Middle East&apos;s insurance landscape since 1964. As Qatar&apos;s first domestic insurer,
                                QIC stepped in to address a crucial gap in the market, providing risk management solutions designed to meet the diverse needs of individuals and
                                businesses.
                            </p>
                            <p className="mt-5">
                                Fast forward more than six decades, and QIC has not only maintained its market leadership in Qatar but also established itself as a dominant force
                                across the GCC and MENA regions.
                            </p>
                        </section>

                        <section id="Challenges">
                            <CaseStudyInformation
                                subTitle={"The challenge: With legacy systems come lost opportunities"}
                                descriptions={[
                                    "As QIC scaled to become the market leader that it is today, they hit a critical pain point. Their legacy systems could not match their rapidly growing customer expectations. They faced several operational challenges:",
                                ]}
                                className="mt-0"
                            />

                            <FeatureList
                                List={[
                                    { listItem: "Slower response times with sluggish communication channels" },
                                    { listItem: "Unstructured, often chaotic communication with customers on personal WhatsApp" },
                                    { listItem: "Unsolicited audio calls from prospects with no way to track or manage them " },
                                    { listItem: "Fragmented workflows with lack of integrated CRMs" },
                                    { listItem: "Lack of data insights for better decision-making." },
                                ]}
                                className="mt-5 bg-gray-100 border-red-500"
                                Icon={<IoIosCheckmarkCircle color="red" />}
                            />
                            <p className="mt-5">The result? High volume of unattended, unmanaged customer enquiries that ultimately failed to convert.</p>
                        </section>

                        <section id="Solutions">
                            <CaseStudyInformation
                                subTitle={"The transformation: Scaling customer conversations towards conversions"}
                                descriptions={[
                                    "To address their mounting operational challenges, QIC strategically transformed their customer communication infrastructure with Gallabox. They:",
                                ]}
                                className="mt-0"
                            />

                            <FeatureList
                                List={[
                                    { listItem: "Replaced personal WhatsApp with a centralized team inbox" },
                                    { listItem: "Implemented their official '800' meta-verified number" },
                                    { listItem: "Setup automated lead qualification" },
                                    { listItem: "Integrated WhatsApp Business with their CRM for contextual customer conversations" },
                                    { listItem: "Enabled AI-powered 24/7 support for instant query management." },
                                ]}
                                className="mt-5 bg-gray-100"
                            />

                            <CaseStudyInformation
                                subTitle={"Why Gallabox emerged as a strategic choice"}
                                descriptions={[
                                    "Gallabox stood out as the perfect solution for QIC's growing needs, offering a seamless blend of automation, integration, and ease of use. Here's why:",
                                ]}
                                className="mt-10"
                            />

                            <CaseStudySubInformation
                                content={[
                                    {
                                        title: "Intuitive user experience",
                                        description: [
                                            "Gallabox's user-friendly interface proved to be an instant success with the QIC team. They found it remarkably easy to navigate and adopt, with little to no learning curve.",
                                        ],
                                    },
                                    {
                                        title: "Seamless CRM integration",
                                        description: [
                                            "Gallabox's native integration with QIC's Zoho CRM system was a game-changer. Unlike other platforms requiring multiple third-party integrations, setting it up was quick and efficient. Their teams now had complete context during conversations, enabling more personalized and effective customer interactions.",
                                        ],
                                    },
                                    {
                                        title: "Unified communication",
                                        description: [
                                            "The ability to consolidate all customer interactions into a single, manageable team inbox was crucial. Gallabox's shared inbox meant no more scattered conversations or lost context — every customer interaction was tracked, managed, and actionable.",
                                        ],
                                    },
                                    {
                                        title: "Powerful AI-powered automations",
                                        description: [
                                            "The depth of Gallabox's automation capabilities set it apart – right  from AI-powered lead qualification, response management, customer engagement, and round the clock customer support — eliminated the manual bottlenecks that were holding their teams back.",
                                        ],
                                    },
                                    {
                                        title: "Proven track record",
                                        description: [
                                            "Gallabox's strong reputation in the market, backed by positive customer reviews across platforms, gave QIC the confidence they needed.",
                                        ],
                                    },
                                ]}
                                className="my-6"
                            />
                            <ClientFeedback
                                descriptions="Gallabox streamlined communication, improved customer service, and increased productivity by 30%. It integrates with CRM, tracks customer interactions, and is user-friendly. If you're looking for a way to improve your productivity and customer service, I highly recommend Gallabox."
                                authorName="Hazim Nazir"
                                designationWithServiceName="Quality and Audit Officer"
                                className="border-blue-700 bg-gray-100"
                            />
                        </section>

                        <section id="Impact">
                            <CaseStudyInformation
                                subTitle={"The Impact: Turning customer conversations into business result"}
                                descriptions={["Gallabox's solutions drove remarkable results for QIC:"]}
                                className="mt-0"
                            />
                            <CaseStudySubInformation
                                content={[
                                    {
                                        title: "31% increase in employee productivity",
                                        description: [
                                            "With auto-assignment of agents and response management, agents could handle more inquiries efficiently while focusing on high-value customer interactions.",
                                        ],
                                    },

                                    {
                                        title: "102% growth in lead generation",
                                        description: [
                                            "By converting unmanaged inquiries into structured conversations, QIC boosted their conversion rates. Auto-capturing & qualification of leads, combined with seamless CRM integration ensured no opportunity got missed.",
                                        ],
                                    },

                                    {
                                        title: "2X improvement in CSAT ratings",
                                        description: [
                                            "Quick response times, 24/7 availability, and contextual conversations significantly doubled the impact of customer conversations and their overall experience.",
                                            "Gallabox delivered exactly what QIC needed: operational excellence, improved customer interactions, and a scalable system that matched their ambitions.",
                                            "QIC continues to partner with Gallabox as they move to their next phase of scale and innovation — with one thing being a constant: their mutual commitment to excellence in every customer conversation.",
                                        ],
                                    },
                                ]}
                                className="my-6"
                            />
                        </section>
                    </div>
                </div>
                <div>
                    <div style={{ position: "sticky", top: "100px", minHeight: "510px" }} className="hidden md:block">
                        <Navbar links={navLinks} />
                    </div>
                </div>
            </section>
            <section className="max-w-6xl mx-auto">
                <div>
                    <h3 className="text-gray-900 max-w-6xl mx-auto mb-10 mt-5 ml-4 text-center md:text-left">Keep exploring stories like this.</h3>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 max-w-6xl mx-auto gap-10 mb-20">
                        {casestudyCardsDetails.map((data, index) => (
                            <CasestudyCard key={index} {...data} />
                        ))}
                    </div>
                </div>
            </section>
        </NewLayout>
    );
};

export default qatarInsurance;
